import React, { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Profile } from "../../../types/types";

import { XMarkIcon } from "@heroicons/react/24/outline";

interface FightScoresProps {
  member: Profile;
  onClose?: () => void;
}

interface Stat {
  wins: number;
  losses: number;
  ko: number;
  tko: number;
  submission: number;
  decision: number;
  draw: number;
  dq: number;
  noContest: number;
  totalFights: number;
}

const BreakdownBar = memo(
  ({
    label,
    value,
    total,
    color,
  }: {
    label: string;
    value: number;
    total: number;
    color: string;
  }) => {
    const percentage = useMemo(() => {
      if (total <= 0) return 0;
      return Math.round((value / total) * 100);
    }, [value, total]);

    return (
      <div className="mb-3">
        <div className="flex justify-between items-center mb-1">
          <span className="text-gray-500 text-xs font-medium">{label}</span>
          <span className="text-gray-500 text-xs">{percentage}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${color}`}
            style={{ width: `${percentage}%` }}
            role="progressbar"
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      </div>
    );
  }
);

const StatTile = ({
  title,
  titleClass,
  bg,
  children,
}: {
  title: string;
  titleClass: string;
  bg: string;
  children: React.ReactNode;
}) => (
  <div
    className={`${bg} rounded-3xl p-4 flex flex-col gap-3 shadow-[inset_0_0_1px_rgba(0,0,0,0.3)]`}
  >
    <div className={`text-sm font-medium ${titleClass}`}>{title}</div>
    {children}
  </div>
);

const Row = ({ label, value }: { label: string; value: number }) => (
  <div className="flex items-center gap-1">
    <span className="text-sm font-medium text-gray-500 pr-2">{label}</span>
    <span className="text-3xl font-extrabold text-gray-900 leading-9">
      {value}
    </span>
  </div>
);

const FightScores: React.FC<FightScoresProps> = ({ member, onClose }) => {
  const { t } = useTranslation("publicProfile");
  const [level, setLevel] = useState<"amateur" | "professional">("amateur");
  const fighterData = member.fighter?.fighterScoreResponseDto;

  if (!fighterData) return null;

  const stats: Stat | undefined = fighterData[level];

  const totalFightsForBars = stats
    ? stats.ko +
      stats.tko +
      stats.submission +
      stats.decision +
      stats.draw +
      stats.dq +
      stats.noContest
    : 0;

  return (
    <div className="flex flex-col gap-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div className="flex justify-between items-center">
        <div className="text-sm font-bold text-gray-900">{t("fight_scores")}</div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="inline-flex self-start gap-0.5 rounded-3xl bg-gray-100 px-2 py-1">
        {(["amateur", "professional"] as const).map((lvl) => (
          <button
            key={lvl}
            type="button"
            onClick={() => setLevel(lvl)}
            className={`px-3 py-1.5 rounded-3xl text-sm font-medium transition-colors ${
              level === lvl
                ? "bg-white text-gray-900 shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
                : "text-gray-500"
            }`}
          >
            {t(lvl)}
          </button>
        ))}
      </div>

      {!stats ? (
        <div className="text-gray-500 text-sm py-6 text-center">
          {t(`no_${level}_scores`)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <StatTile
              title={t("wins")}
              titleClass="text-green-600"
              bg="bg-green-50"
            >
              <Row
                label={t("stoppage", { defaultValue: "Stoppage" })}
                value={stats.ko + stats.tko + stats.submission}
              />
              <Row label={t("decision")} value={stats.decision} />
            </StatTile>
            <StatTile
              title={t("losses")}
              titleClass="text-red-600"
              bg="bg-red-50"
            >
              <Row label={t("total", { defaultValue: "Total" })} value={stats.losses} />
            </StatTile>
            <StatTile
              title={t("draw")}
              titleClass="text-gray-900"
              bg="bg-gray-50"
            >
              <span className="text-3xl font-extrabold text-gray-900 leading-9">
                {stats.draw}
              </span>
            </StatTile>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 mt-2">
            <div>
              <BreakdownBar label={t("ko")} value={stats.ko} total={totalFightsForBars} color="bg-orange-400" />
              <BreakdownBar label={t("tko")} value={stats.tko} total={totalFightsForBars} color="bg-orange-300" />
              <BreakdownBar label={t("submission")} value={stats.submission} total={totalFightsForBars} color="bg-purple-400" />
              <BreakdownBar label={t("decision")} value={stats.decision} total={totalFightsForBars} color="bg-blue-400" />
            </div>
            <div>
              <BreakdownBar label={t("draw")} value={stats.draw} total={totalFightsForBars} color="bg-gray-400" />
              <BreakdownBar label={t("dq")} value={stats.dq} total={totalFightsForBars} color="bg-red-400" />
              <BreakdownBar label={t("no_contest")} value={stats.noContest} total={totalFightsForBars} color="bg-yellow-400" />
              <div className="flex justify-between items-center p-2 rounded-xl bg-blue-50">
                <span className="text-gray-900 font-medium text-xs">
                  {t("total_fights")}
                </span>
                <span className="text-gray-900 font-semibold text-sm">
                  {stats.totalFights}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FightScores;